import { useEffect, useState } from 'react';
import { Link, useOutletContext, useSearchParams } from 'react-router-dom';
import { api } from '../api/client.js';
import {
  formatDueDateBr,
  formatSubscriptionAmount,
  openSubscriptionPayment,
} from '../utils/subscriptionBilling.js';

export function SubscriptionBillingPage() {
  const { user } = useOutletContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [payBusyId, setPayBusyId] = useState(null);

  async function reload() {
    setLoading(true);
    setError('');
    try {
      const data = await api('/tenants');
      setTenants(Array.isArray(data?.tenants) ? data.tenants : []);
    } catch (err) {
      setError(err?.message || 'Não foi possível carregar as cobranças.');
      setTenants([]);
    } finally {
      setLoading(false);
    }
  }

  async function payTenant(tenantId) {
    setPayBusyId(tenantId);
    setError('');
    try {
      const url = await openSubscriptionPayment(tenantId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(err?.message || 'Não foi possível iniciar o pagamento.');
    } finally {
      setPayBusyId(null);
    }
  }

  useEffect(() => {
    reload().catch(() => {});
  }, []);

  useEffect(() => {
    const billing = searchParams.get('billing');
    const tenantPay = searchParams.get('tenant');

    if (billing === 'pay' && tenantPay) {
      const rowId = Number(tenantPay);
      if (Number.isFinite(rowId) && rowId > 0) {
        const next = new URLSearchParams(searchParams);
        next.delete('billing');
        next.delete('tenant');
        setSearchParams(next, { replace: true });
        payTenant(rowId);
      }
      return;
    }

    if (!billing) return;

    if (billing === 'paid') {
      setNotice(
        'Pagamento registado — se o Mercado Pago já confirmou, a loja será desbloqueada em poucos instantes.',
      );
      reload().catch(() => {});
    } else if (billing === 'failure') {
      setNotice('Pagamento não concluído. Pode tentar novamente abaixo.');
    } else if (billing === 'pending') {
      setNotice('Pagamento pendente no Mercado Pago. Quando confirmar, a loja será regularizada.');
    }

    const next = new URLSearchParams(searchParams);
    next.delete('billing');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deep-link de retorno MP
  }, [searchParams, setSearchParams]);

  const billableTenants = tenants.filter(
    (t) => Number(t.sub_pending_cnt) > 0 || Number(t.billing_overdue_blocked) === 1,
  );

  const isSuperAdmin = user?.role === 'super_admin';

  return (
    <div className="space-y-6 py-8">
      <div className="max-w-2xl space-y-2">
        <h2 className="font-display text-3xl font-semibold text-ink dark:text-mist">Mensalidade da plataforma</h2>
        <p className="text-sm leading-relaxed text-ink/65 dark:text-frost-muted">
          Pague aqui dentro do painel — <strong className="font-semibold text-ink dark:text-mist">não precisa do e-mail</strong>{' '}
          do dono da loja. Quando a mensalidade atrasa, produtos, pedidos e a loja pública ficam pausados até
          regularizar.
        </p>
      </div>

      {notice ? (
        <div className="rounded-2xl border border-emerald-500/35 bg-emerald-500/10 px-4 py-3 text-sm font-semibold text-emerald-900 dark:text-emerald-100">
          {notice}
        </div>
      ) : null}

      {error ? (
        <div className="rounded-2xl border border-accent2/40 bg-accent2/15 px-4 py-3 text-sm font-semibold text-accent2">
          {error}
        </div>
      ) : null}

      {loading ? (
        <p className="text-sm font-semibold text-ink/55 dark:text-frost-muted">A carregar cobranças…</p>
      ) : billableTenants.length === 0 ? (
        <div className="rounded-2xl border border-ink/10 bg-white/70 px-5 py-6 text-sm dark:border-white/10 dark:bg-night-elevated/80">
          <p className="font-semibold text-ink dark:text-mist">Nenhuma mensalidade em aberto no momento.</p>
          <p className="mt-2 text-ink/60 dark:text-frost-muted">
            Quando houver cobrança pendente ou loja suspensa, ela aparecerá aqui com botão de pagamento.
          </p>
          <Link
            to="/app/empresa"
            className="mt-4 inline-flex rounded-xl border border-ink/15 bg-white px-4 py-2 text-xs font-semibold text-ink dark:border-white/15 dark:bg-white/10 dark:text-mist"
          >
            Ir para Empresas
          </Link>
        </div>
      ) : (
        <div className="grid gap-4">
          {billableTenants.map((t) => {
            const blocked = Number(t.billing_overdue_blocked) === 1;
            const hasPending = Number(t.sub_pending_cnt) > 0;
            return (
              <article
                key={t.id}
                className={`rounded-2xl border px-5 py-4 shadow-sm ${
                  blocked
                    ? 'border-rose-500/35 bg-gradient-to-br from-rose-500/10 to-orange-500/5 dark:from-rose-950/30 dark:to-orange-950/20'
                    : 'border-violet-500/25 bg-white/75 dark:border-violet-400/20 dark:bg-night-elevated/80'
                }`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-display text-lg font-semibold text-ink dark:text-mist">{t.nome}</h3>
                      {blocked ? (
                        <span className="rounded-full bg-rose-600/15 px-2 py-0.5 text-[10px] font-bold uppercase text-rose-900 dark:text-rose-100">
                          loja suspensa
                        </span>
                      ) : (
                        <span className="rounded-full bg-violet-600/15 px-2 py-0.5 text-[10px] font-bold uppercase text-violet-950 dark:text-violet-100">
                          em aberto
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-ink/55 dark:text-frost-muted">/{t.slug}</p>
                  </div>
                </div>

                {hasPending ? (
                  <p className="mt-3 text-sm text-ink/80 dark:text-mist/85">
                    Valor pendente:{' '}
                    <span className="font-semibold text-ink dark:text-mist">
                      {formatSubscriptionAmount(t.sub_pending_sum_brl)}
                    </span>
                    {t.sub_next_due_date ? (
                      <>
                        {' '}
                        · vencimento{' '}
                        <span className="font-mono">{formatDueDateBr(t.sub_next_due_date)}</span>
                      </>
                    ) : null}
                  </p>
                ) : (
                  <p className="mt-3 text-sm text-ink/60 dark:text-frost-muted">
                    Loja suspensa por atraso — regularize a mensalidade para voltar a operar.
                  </p>
                )}

                {hasPending ? (
                  <button
                    type="button"
                    disabled={payBusyId != null}
                    onClick={() => payTenant(t.id)}
                    className="mt-4 inline-flex w-full items-center justify-center rounded-xl bg-ink px-4 py-3 text-xs font-bold uppercase tracking-wide text-mist shadow-sm transition hover:opacity-90 disabled:opacity-60 dark:bg-mist dark:text-ink sm:w-auto"
                  >
                    {payBusyId != null && Number(payBusyId) === Number(t.id)
                      ? 'A preparar checkout…'
                      : 'Pagar mensalidade agora'}
                  </button>
                ) : null}

                {isSuperAdmin ? (
                  <p className="mt-3 text-[11px] text-ink/45 dark:text-frost-muted">
                    Visualização admin — o pagamento abre o checkout Mercado Pago desta empresa.
                  </p>
                ) : null}
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
